import { CheckCircle } from "@mui/icons-material";
import { Alert, Link, Stack, Typography } from "@mui/joy";
import { Outlet, useNavigate, useParams } from "@tanstack/react-router";
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { format } from "date-fns";
import { HTTPError } from "griptonite-api-client/client";
import { ApiCreditAccountAdmin } from "griptonite-api-client/types";
import { useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";

import { useCreditSystemAccountsQuery, useVenueCreditSystemQuery } from "@/hooks/credits";
import { useCurrentVenueQuery } from "@/hooks/venues";

import Avatar from "../Avatar";
import { SUPPORT_EMAIL } from "../ManageCreditSystem/ManageCreditSystem";
import AccountStatusChip from "./AccountStatusChip";
import LoyaltyNavToggle from "./LoyaltyNavToggle";
import SortableTable, { normalizeForSearch, StickySearchBar } from "./SortableTable";

const columnHelper = createColumnHelper<ApiCreditAccountAdmin>();

const columns = [
  columnHelper.accessor((account) => account.user.name, {
    id: "name",
    header: "Name",
    sortingFn: "text",
    meta: { minWidth: 200 },
    cell: ({ row }) => (
      <Stack direction="row" alignItems="center" gap={1.5}>
        <Avatar user={row.original.user} size={32} />
        <Typography fontWeight="bold" noWrap sx={{ minWidth: 0 }}>
          {row.original.user.name}
        </Typography>
      </Stack>
    ),
  }),
  columnHelper.accessor("balance", {
    header: "Current balance",
    meta: { width: 150 },
    cell: ({ getValue }) => <Typography fontWeight="bold">{getValue()}</Typography>,
  }),
  columnHelper.accessor("creditsEarned30d", {
    header: "30d earnings",
    meta: { width: 150 },
  }),
  // Undefined rather than null, so accounts with no activity sort last in both directions.
  columnHelper.accessor((account) => account.lastActivityAt ?? undefined, {
    id: "lastActivity",
    header: "Last activity",
    sortingFn: "datetime",
    meta: { width: 170 },
    sortUndefined: "last",
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? (
        <Typography noWrap level="inherit">
          {format(new Date(value), "d MMM yyyy, HH:mm")}
        </Typography>
      ) : (
        <Typography textColor="neutral.500">Never</Typography>
      );
    },
  }),
  columnHelper.accessor("status", {
    header: "Status",
    sortingFn: "text",
    meta: { width: 120 },
    cell: ({ getValue }) => <AccountStatusChip status={getValue()} />,
  }),
  columnHelper.accessor((account) => account.checkinAccount !== null, {
    id: "membershipLinked",
    header: "Membership linked",
    sortingFn: "basic",
    meta: { width: 170 },
    cell: ({ getValue }) =>
      getValue() ? <CheckCircle color="success" aria-label="Linked" /> : null,
  }),
];

// Clicking a row opens the account in the side panel.
const AccountsTable = ({
  accounts,
  venueUuid,
}: {
  accounts: ApiCreditAccountAdmin[];
  venueUuid: string;
}) => {
  const navigate = useNavigate();
  const openAccountUuid = useParams({ strict: false }).accountUuid;
  const table = useReactTable({
    columns,
    data: accounts,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
    // Matches the API's default order.
    initialState: { sorting: [{ id: "lastActivity", desc: true }] },
  });

  return (
    <SortableTable
      table={table}
      backgroundColor={"var(--joy-palette-neutral-50)"}
      isRowOpen={(account) => account.uuid === openAccountUuid}
      onRowClick={(account) =>
        navigate({
          to: "/gym/$venueUuid/loyalty/accounts/$accountUuid",
          params: { venueUuid, accountUuid: account.uuid },
        })
      }
    />
  );
};

export default function AccountsOverview() {
  const { data: venue } = useCurrentVenueQuery();
  const systemQuery = useVenueCreditSystemQuery(venue?.uuid);
  const accountsQuery = useCreditSystemAccountsQuery(systemQuery.data?.slug);

  // The input updates as the user types; the filter waits for a pause.
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const updateSearchQuery = useDebouncedCallback(setSearchQuery, 150);

  const accounts = useMemo(() => {
    const query = normalizeForSearch(searchQuery.trim());
    if (!query) return accountsQuery.data;
    return accountsQuery.data?.filter(
      ({ user }) =>
        normalizeForSearch(user.name).includes(query) ||
        normalizeForSearch(user.email).includes(query),
    );
  }, [accountsQuery.data, searchQuery]);

  let content;
  if (systemQuery.error instanceof HTTPError && systemQuery.error.response.status === 404) {
    content = (
      <Alert color="neutral">
        <Typography>
          Credits aren’t set up for this gym yet. To get started, contact{" "}
          <Link href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</Link>.
        </Typography>
      </Alert>
    );
  } else if (systemQuery.error || accountsQuery.error) {
    content = <Alert color="danger">Couldn’t load the accounts.</Alert>;
  } else if (venue && accounts) {
    if (!accountsQuery.data?.length) {
      content = <Typography textColor="neutral.500">No accounts yet.</Typography>;
    } else if (!accounts.length) {
      content = <Typography textColor="neutral.500">No accounts match this search.</Typography>;
    } else {
      content = <AccountsTable accounts={accounts} venueUuid={venue.uuid} />;
    }
  }

  // The panel for a chosen account renders in the outlet, beside the table.
  return (
    <Stack direction="row" sx={{ height: "100%" }}>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          height: "100%",
          overflowY: "auto",
          px: 3,
          pb: 3,
        }}
      >
        <Stack direction="row" alignItems="center" gap={1} sx={{ pt: 3 }}>
          <LoyaltyNavToggle />
          <Typography level="h3">Accounts</Typography>
        </Stack>
        <StickySearchBar
          backgroundColor="neutral.50"
          placeholder="Search by name or email"
          value={searchInput}
          onChange={(event) => {
            setSearchInput(event.target.value);
            updateSearchQuery(event.target.value);
          }}
        />
        {content}
      </Stack>
      <Outlet />
    </Stack>
  );
}
